import { useCallback, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { refreshFromServer, useDataRefresh } from '../hooks/useDataRefresh';
import { useApi } from '../hooks/useApi';

/*
 * Sidebar nav — grouped into sections. Ids match PAGES in App.jsx.
 *
 * This array is the CATALOGUE: which pages exist, what they're called, what
 * order they're in. Their STATUS — live / preview / building / hidden — is data
 * (commercial.AppTab, migration 069), edited on the Governance page. Matthew,
 * 18 Sep: "we only show the ones in use ... have the ability to flag as not
 * active or still building. Would be nice to have control over this to change."
 *
 * The split is on purpose. A page only exists because someone wrote it here, so
 * the catalogue has to live in code or a new page would be invisible until
 * somebody remembered to seed a row. The status is a judgement that changes
 * weekly, so it has to live in data or it needs a deploy.
 *
 * ⚠ Do not reintroduce `preview: true` here — migration 069 moved those six
 * into the table, and a flag in both places is a flag that will disagree.
 */
export const SECTIONS = [
  { label: 'Overview', tabs: [
    { id: 'overview',          label: 'Business Overview' },
    { id: 'key-dates',         label: 'Key Dates' },
  ]},
  { label: 'Sales & Forecasting', tabs: [
    { id: 'nam',               label: 'Scan Sales' },
    { id: 'forecast',          label: 'Scan Sales Forecast' },
    { id: 'trends',            label: 'Market Trends' },
  ]},
  { label: 'Retail Execution', tabs: [
    { id: 'distribution',      label: 'Distribution' },
    { id: 'stores',            label: 'Store Performance' },
    { id: 'merchandising',     label: 'Merchandising' },
    { id: 'space-planning',    label: 'Space Planning' },
    { id: 'retailer-profiles', label: 'Retailer Profiles' },
  ]},
  { label: 'Trade & Marketing', tabs: [
    { id: 'promotions',        label: 'Promotions' },
    { id: 'marketing-calendar', label: 'Marketing Calendar' },
    { id: 'activity-calendar', label: 'Activity Calendar' },
    { id: 'promo-roi',         label: 'Promo Effectiveness' },
    { id: 'pricing',           label: 'Pricing' },
    { id: 'cost-mix',          label: 'Cost Mix' },
  ]},
  { label: 'Commercial', tabs: [
    { id: 'commercials',       label: 'Commercials' },
    { id: 'product-details',   label: 'Product Details' },
  ]},
  { label: 'Master Data', tabs: [
    { id: 'master-data-ai',    label: 'Master Data AI' },
    { id: 'store-master',      label: 'Store Master' },
    { id: 'ranging-matrix',    label: 'Ranging Matrix' },
    { id: 'scan-items',        label: 'Scan Items' },
    { id: 'rebate-master',     label: 'Rebate Master' },
    { id: 'growth-grid',       label: 'Growth Grid' },
    { id: 'transport-allocation', label: 'Transport Allocation' },
    { id: 'allocation-review', label: 'Allocation Review' },
    { id: 'rrp-items',         label: 'RRP Items' },
    { id: 'promo-rrp',         label: 'Promo RRP' },
    { id: 'customer-dcs',      label: 'Customer DCs' },
  ]},
  { label: 'Customer & Growth', tabs: [
    { id: 'crm',               label: 'CRM' },
    { id: 'new-business',      label: 'New Business' },
    { id: 'export-customers',  label: 'Export Customers' },
    { id: 'channel-strategy',  label: 'Channel Strategy' },
  ]},
  { label: 'Team', tabs: [
    { id: 'capacity-review',   label: 'Capacity Review' },
    { id: 'ai-projects',       label: 'AI Projects' },
  ]},
  { label: 'Insights', tabs: [
    { id: 'recommendations',   label: 'Recommendations' },
    { id: 'dr',                label: 'Demand Review' },
  ]},
  { label: 'System', tabs: [
    { id: 'governance',        label: 'Governance' },
    { id: 'data-sources',      label: 'Data Sources' },
  ]},
];

/*
 * The four statuses, in one place, so the sidebar badge and the Governance
 * editor are guaranteed to describe the same thing.
 *   live     — normal. Stored as the ABSENCE of a row (the API deletes on 'live').
 *   preview  — the amber badge that already existed: in SQL, not yet in the plan.
 *   building — visible so people can watch it arrive, and know not to trust it yet.
 *   hidden   — gone from the sidebar. Still reachable if it is the open tab.
 */
export const TAB_STATUS = {
  live:     { label: 'Live',     badge: null,       hint: 'In use and maintained' },
  preview:  { label: 'Preview',  badge: 'Preview',  hint: 'Saved to SQL but not yet feeding the demand plan' },
  building: { label: 'Building', badge: 'Building', hint: 'Still being built — not ready to rely on' },
  hidden:   { label: 'Hidden',   badge: null,       hint: 'Not shown in the sidebar' },
};

/**
 * Tab statuses from commercial.AppTab (069), as { tabId: {status, note} }.
 *
 * A failed or still-loading fetch yields {} — every tab live, nothing hidden.
 * That is the right way to fail: a navigation control that disappears because
 * an API call timed out is worse than one that briefly shows too much.
 */
export function useTabStatus() {
  const apiFetch = useApi();
  const { data } = useQuery({
    queryKey: ['app-tabs'],
    queryFn: async () => {
      const r = await apiFetch('/app-tabs');
      if (!r.ok) throw new Error(`API ${r.status}`);
      return r.json();
    },
    staleTime: 60 * 1000,
    retry: 1,
  });
  return useMemo(() => {
    const m = {};
    for (const row of data ?? []) m[row.tabId] = { status: row.status, note: row.note };
    return m;
  }, [data]);
}

export default function Layout({ activeTab, onTabChange, theme, onToggleTheme, children }) {
  const { lastRefresh } = useDataRefresh(60);
  const isLight = theme === 'light';
  const [collapsed, setCollapsed] = useState(false);
  const tabStatus = useTabStatus();

  // Real data freshness from commercial.JsonStore (#2) — replaces the misleading
  // client reload time. Headline = newest blob; tooltip lists every dataset.
  const apiFetch = useApi();
  const { data: freshness } = useQuery({
    queryKey: ['data-freshness'],
    queryFn: async () => (await apiFetch('/data-freshness')).json(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  const dataAsOf = freshness?.length
    ? new Date(Math.max(...freshness.map((r) => new Date(r.updatedAt).getTime())))
    : null;
  const freshnessTitle = freshness?.length
    ? freshness.map((r) => `${r.key}: ${new Date(r.updatedAt).toLocaleString('en-AU')}`).join('\n')
    : 'Per-dataset refresh times';

  // Active tab's label — shown in the page header as the current-page title.
  const activeLabel = SECTIONS.flatMap(s => s.tabs).find(t => t.id === activeTab)?.label ?? '';
  /*
   * Hide from the sidebar, but never from the page you are on: if someone hides
   * the tab you have open, the content stays put and the entry simply stops
   * being offered. Hiding a tab is a tidy-up, not an eviction.
   */
  const visibleTabs = useCallback(
    (tabs) => tabs.filter(t => tabStatus[t.id]?.status !== 'hidden' || t.id === activeTab),
    [tabStatus, activeTab],
  );
  const sidebarWidth = collapsed ? 64 : 240;

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [scriptStatus, setScriptStatus] = useState({});
  const [logLines, setLogLines] = useState([]);
  const [refreshError, setRefreshError] = useState(null);
  const [refreshFinished, setRefreshFinished] = useState(null);
  const logRef = useRef(null);

  const handleEvent = useCallback((ev) => {
    if (ev.step === 'start') {
      setScriptStatus(s => ({ ...s, [ev.script]: { label: ev.label, state: 'running' } }));
    } else if (ev.step === 'log') {
      setLogLines(ls => {
        const next = ls.concat([{ script: ev.script, line: ev.line }]);
        return next.length > 500 ? next.slice(-500) : next;
      });
    } else if (ev.step === 'done' || ev.step === 'error') {
      setScriptStatus(s => ({
        ...s,
        [ev.script]: {
          label: ev.label,
          state: ev.ok ? 'done' : 'error',
          elapsed: ev.elapsed_sec,
          rc: ev.rc,
        },
      }));
    } else if (ev.step === 'finished') {
      setRefreshFinished(ev);
    }
    requestAnimationFrame(() => {
      if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
    });
  }, []);

  const triggerRefresh = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    setScriptStatus({});
    setLogLines([]);
    setRefreshError(null);
    setRefreshFinished(null);
    try {
      await refreshFromServer(handleEvent);
    } catch (err) {
      setRefreshError(err.message || String(err));
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, handleEvent]);

  const allOk = refreshFinished?.results?.every(r => r.ok);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex' }}>
      {/* ── Sidebar ────────────────────────────────────────────────── */}
      <aside
        style={{
          width: sidebarWidth,
          minWidth: sidebarWidth,
          background: 'var(--bg-sidebar)',
          borderRight: '1px solid var(--border)',
          position: 'sticky',
          top: 0,
          height: '100vh',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.2s',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: collapsed ? '18px 0' : '18px 20px',
            justifyContent: collapsed ? 'center' : 'flex-start',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <img
            src="/majans-logo.png"
            alt="Majans"
            style={{ width: 32, height: 32, borderRadius: 6, display: 'block' }}
          />
          {!collapsed && (
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15 }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
                Majans
              </span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Commercial
              </span>
            </div>
          )}
        </div>

        <nav style={{ flex: 1, padding: collapsed ? '12px 8px' : '12px 12px' }}>
          {SECTIONS.map(section => (
            <div key={section.label} style={{ marginBottom: 14 }}>
              {!collapsed && visibleTabs(section.tabs).length > 0 && (
                <div
                  style={{
                    fontSize: 10,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    color: 'var(--text-muted)',
                    padding: '6px 10px',
                    fontWeight: 600,
                  }}
                >
                  {section.label}
                </div>
              )}
              {visibleTabs(section.tabs).map(tab => {
                const active = activeTab === tab.id;
                const st = tabStatus[tab.id]?.status ?? 'live';
                const meta = TAB_STATUS[st] ?? TAB_STATUS.live;
                return (
                  <button
                    key={tab.id}
                    onClick={() => onTabChange(tab.id)}
                    title={collapsed ? tab.label : undefined}
                    className={active ? 'nav-item nav-item-active' : 'nav-item'}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      padding: collapsed ? '8px 0' : '7px 10px',
                      justifyContent: collapsed ? 'center' : 'flex-start',
                      border: 'none',
                      background: active ? 'var(--nav-active-bg)' : 'transparent',
                      color: active ? 'var(--nav-active-text)' : 'var(--text-body)',
                      borderRadius: 6,
                      fontSize: 13,
                      fontWeight: active ? 600 : 400,
                      cursor: 'pointer',
                      textAlign: 'left',
                      marginBottom: 2,
                      transition: 'background 0.12s, color 0.12s',
                    }}
                  >
                    {collapsed
                      ? <span style={{ fontSize: 11, fontWeight: 600 }}>
                          {tab.label.split(' ').map(w => w[0]).slice(0, 2).join('')}
                        </span>
                      : <>
                          <span style={{ flex: 1 }}>{tab.label}</span>
                          {meta.badge && (
                            <span title={tabStatus[tab.id]?.note || meta.hint}
                              style={{ fontSize: 8.5, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', padding: '1px 5px', borderRadius: 999,
                                background: st === 'building' ? 'var(--info-soft)' : 'var(--warn-soft)',
                                color: st === 'building' ? 'var(--info)' : 'var(--warn-text)',
                                border: `1px solid ${st === 'building' ? 'var(--info-border)' : 'var(--warn-border)'}` }}>
                              {meta.badge}
                            </span>
                          )}
                          {/* A tab you can still see only because you are standing on it. */}
                          {st === 'hidden' && (
                            <span title="Hidden from the sidebar — you're seeing it because it's the open tab"
                              style={{ fontSize: 8.5, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', padding: '1px 5px', borderRadius: 999, background: 'var(--border)', color: 'var(--text-muted)', border: '1px solid var(--border-soft)' }}>
                              Hidden
                            </span>
                          )}
                        </>}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        <button
          onClick={() => setCollapsed(c => !c)}
          style={{
            margin: collapsed ? '0 auto 12px' : '0 12px 12px',
            padding: '6px 10px',
            background: 'transparent',
            border: '1px solid var(--border-soft)',
            borderRadius: 6,
            color: 'var(--text-muted)',
            cursor: 'pointer',
            fontSize: 12,
          }}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? '›' : '‹ Collapse'}
        </button>
      </aside>

      {/* ── Main column ────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <header
          style={{
            background: 'var(--bg-header)',
            borderBottom: '1px solid var(--border)',
            padding: '0 24px',
            height: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
              {activeLabel}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ color: 'var(--text-muted)', fontSize: 12 }} title={freshnessTitle}>
              {dataAsOf
                ? `Data as of ${dataAsOf.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' })}`
                : 'Data age unknown'}
            </span>
            <button
              onClick={triggerRefresh}
              disabled={isRefreshing}
              title={isRefreshing ? 'Refresh in progress…' : 'Rebuild all dashboard data sources'}
              style={{
                background: 'var(--bg-input)',
                border: '1px solid var(--border-soft)',
                borderRadius: 6,
                padding: '5px 12px',
                color: 'var(--text-body)',
                fontSize: 12,
                cursor: isRefreshing ? 'progress' : 'pointer',
                opacity: isRefreshing ? 0.6 : 1,
              }}
            >
              {isRefreshing ? '⟳ Refreshing…' : '↻ Refresh'}
            </button>
            <button
              onClick={onToggleTheme}
              title={isLight ? 'Switch to dark mode' : 'Switch to light mode'}
              style={{
                background: 'var(--bg-input)',
                border: '1px solid var(--border-soft)',
                borderRadius: 6,
                padding: '5px 10px',
                color: 'var(--text-body)',
                fontSize: 14,
                cursor: 'pointer',
                lineHeight: 1,
              }}
            >
              {isLight ? '🌙' : '☀️'}
            </button>
          </div>
        </header>

        <main style={{ padding: '24px', flex: 1 }}>{children}</main>
      </div>

      {/* ── Refresh progress panel ─────────────────────────────────── */}
      {(isRefreshing || refreshFinished || refreshError) && (
        <RefreshPanel
          isRefreshing={isRefreshing}
          scriptStatus={scriptStatus}
          logLines={logLines}
          logRef={logRef}
          error={refreshError}
          finished={refreshFinished}
          allOk={allOk}
          onClose={() => { setRefreshFinished(null); setRefreshError(null); setScriptStatus({}); setLogLines([]); }}
          onReload={() => window.location.reload()}
        />
      )}
    </div>
  );
}

function RefreshPanel({ isRefreshing, scriptStatus, logLines, logRef, error, finished, allOk, onClose, onReload }) {
  const entries = Object.entries(scriptStatus);
  return (
    <div style={{
      position: 'fixed', right: 16, bottom: 16, width: 460, maxHeight: '70vh',
      background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8,
      boxShadow: '0 8px 24px rgba(0,0,0,0.20)', display: 'flex', flexDirection: 'column', zIndex: 50,
    }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
          {isRefreshing ? 'Refreshing data sources…' : error ? 'Refresh failed' : allOk ? 'Refresh complete' : 'Refresh finished with errors'}
        </span>
        {!isRefreshing && (
          <button onClick={onClose} title="Dismiss" style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 14, cursor: 'pointer' }}>×</button>
        )}
      </div>

      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
        {entries.length === 0 && isRefreshing && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Starting…</div>
        )}
        {entries.map(([script, st]) => (
          <div key={script} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, padding: '3px 0' }}>
            <span style={{ color: 'var(--text-body)' }}>
              <StatusDot state={st.state} /> {st.label}
            </span>
            <span style={{ color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: 11 }}>
              {st.state === 'running' ? '…' : st.state === 'done' ? `${st.elapsed ?? '?'}s ✓` : `rc=${st.rc} ✗`}
            </span>
          </div>
        ))}
      </div>

      {error && (
        <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', color: 'var(--danger)', fontSize: 12 }}>
          {error}
        </div>
      )}

      <div ref={logRef} style={{ flex: 1, overflow: 'auto', padding: '8px 14px', fontFamily: 'monospace', fontSize: 11, color: 'var(--text-muted)', minHeight: 120 }}>
        {logLines.map((l, i) => (
          <div key={i} style={{ whiteSpace: 'pre-wrap' }}>
            <span style={{ color: 'var(--text-subtle, #6b7280)' }}>[{l.script.replace('.py', '').replace('build_', '')}]</span> {l.line}
          </div>
        ))}
      </div>

      {finished && !isRefreshing && (
        <div style={{ padding: '10px 14px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
          <span style={{ color: 'var(--text-muted)' }}>
            Total: {finished.total_elapsed_sec}s
          </span>
          <button
            onClick={onReload}
            style={{ background: 'var(--bg-input)', border: '1px solid var(--border-soft)', borderRadius: 6, padding: '4px 12px', color: 'var(--text-body)', fontSize: 12, cursor: 'pointer' }}
          >
            Reload dashboard
          </button>
        </div>
      )}
    </div>
  );
}

function StatusDot({ state }) {
  const color = state === 'running' ? 'var(--info)' : state === 'done' ? 'var(--success)' : state === 'error' ? 'var(--danger)' : 'var(--text-muted)';
  return (
    <span style={{
      display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
      background: color, marginRight: 6,
      animation: state === 'running' ? 'pulse 1.2s ease-in-out infinite' : 'none',
    }} />
  );
}
